import { useEffect, useRef, useState } from "react";
import Lenis from "lenis";
import axios from "axios";
import "@/App.css";
import { Hero } from "@/components/Hero";
import CrmApp from "@/crm/CrmApp";

const IS_CRM =
  window.location.hostname.startsWith("crm.") ||
  window.location.pathname.startsWith("/crm");
import { Marquee } from "@/components/Marquee";
import { SideNav, MobileNav } from "@/components/SideNav";
import { CoreSections } from "@/components/sections/Core";
import { Rentabilidad } from "@/components/sections/Rentabilidad";
import { Valor } from "@/components/sections/Valor";
import { Cierre } from "@/components/sections/Cierre";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CHAPTERS = [
  { id: "01", title: "Resumen Ejecutivo" },
  { id: "02", title: "El Proyecto" },
  { id: "03", title: "Mercado de Lombok" },
  { id: "04", title: "Drivers de Crecimiento" },
  { id: "05", title: "Supuestos del Modelo" },
  { id: "06", title: "Estructura de Costes" },
  { id: "07", title: "Reparto de Ingresos" },
  { id: "08", title: "Hitos de Pago" },
  { id: "09", title: "Escenarios de Rentabilidad" },
  { id: "10", title: "Análisis de Sensibilidad" },
  { id: "11", title: "Alquiler a Largo Plazo" },
  { id: "12", title: "Canales de Comercialización" },
  { id: "13", title: "Revalorización" },
  { id: "14", title: "Retorno Total" },
  { id: "15", title: "Comparativa de Inversión" },
  { id: "16", title: "Plan de Salida" },
  { id: "17", title: "Estructura Legal" },
  { id: "18", title: "Factores de Riesgo" },
  { id: "19", title: "Contacto" },
  { id: "20", title: "Referencias" },
];

function DossierApp() {
  const [dossier, setDossier] = useState(null);
  const [error, setError] = useState(false);
  const [active, setActive] = useState("01");
  const lenisRef = useRef(null);

  useEffect(() => {
    const lenis = new Lenis({ duration: 1.15, smoothWheel: true });
    lenisRef.current = lenis;
    let raf;
    const loop = (t) => {
      lenis.raf(t);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
    };
  }, []);

  useEffect(() => {
    axios
      .get(`${API}/dossier`)
      .then((r) => setDossier(r.data))
      .catch(() => setError(true));
  }, []);

  useEffect(() => {
    if (!dossier) return;
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) setActive(e.target.id.replace("chapter-", ""));
        });
      },
      { rootMargin: "-30% 0px -60% 0px" }
    );
    CHAPTERS.forEach((c) => {
      const el = document.getElementById(`chapter-${c.id}`);
      if (el) obs.observe(el);
    });
    return () => obs.disconnect();
  }, [dossier]);

  const goTo = (id) => {
    const el = document.getElementById(`chapter-${id}`);
    if (el && lenisRef.current) lenisRef.current.scrollTo(el, { offset: -8 });
  };

  if (error) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-ivory px-6 text-center">
        <div>
          <p className="font-serif text-2xl text-jade">Jade Residence</p>
          <p data-testid="dossier-error" className="mt-3 text-sm text-graphite/60">
            No se pudo cargar el dossier. Inténtalo de nuevo en unos segundos.
          </p>
        </div>
      </div>
    );
  }

  if (!dossier) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-ivory">
        <p data-testid="dossier-loading" className="animate-pulse font-serif text-3xl text-jade">
          Jade <span className="italic text-gold">Residence</span>
        </p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-ivory text-graphite">
      <SideNav chapters={CHAPTERS} active={active} onNavigate={goTo} />
      <MobileNav chapters={CHAPTERS} active={active} onNavigate={goTo} />
      <main className="lg:pl-72">
        <Hero meta={dossier.meta} kpis={dossier.heroKpis} onExplore={() => goTo("01")} />
        <Marquee />
        <div className="mx-auto max-w-5xl px-5 sm:px-10">
          <CoreSections d={dossier} />
          <Rentabilidad d={dossier} />
          <Valor d={dossier} />
          <Cierre d={dossier} />
        </div>
        <footer className="border-t border-sand bg-ivory px-5 py-10 sm:px-10">
          <div className="mx-auto max-w-5xl">
            <p className="font-serif text-lg text-jade">
              Jade <span className="italic text-gold">Residence</span>
            </p>
            <p data-testid="legal-disclaimer" className="mt-4 max-w-3xl text-xs leading-relaxed text-graphite/50">
              {dossier.disclaimer}
            </p>
          </div>
        </footer>
      </main>
    </div>
  );
}

export default function App() {
  return IS_CRM ? <CrmApp /> : <DossierApp />;
}
